/**
 * Home ViewModel
 * Contains ALL business logic for the home feature
 */

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../hooks";
import { selectSteContext, selectIsAuthenticated, logout } from "../../auth";
import { useGetSteConfigQuery } from "../../../api";
import { useSipContext } from "../../../services/sip";
import { Analytics } from "../../../services/analytics";
import { Routes } from "../../../routes";
import type { HomeConfig, Contact } from "../model/HomeModel";
import { defaultHomeConfig } from "../model/HomeModel";

// View State
export interface HomeViewState {
  config: HomeConfig;
  contacts: Contact[];
  isLoading: boolean;
  error: string | null;
  agentName: string;
  isAuthenticated: boolean;
  // SIP State
  isSipConnected: boolean;
  isSipRegistered: boolean;
  isMicrophoneGranted: boolean;
}

// View Actions
export interface HomeViewActions {
  handleLogout: () => void;
  handleCall: (contact: Contact) => void;
  handleRefresh: () => void;
  handleRequestMicrophone: () => void;
}

export interface HomeViewModel extends HomeViewState, HomeViewActions {}

interface UseHomeViewModelOptions {
  config?: Partial<HomeConfig>;
}

/**
 * Custom hook that provides the ViewModel for the Home page
 */
export function useHomeViewModel(
  options: UseHomeViewModelOptions = {},
): HomeViewModel {
  const { config: customConfig } = options;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux state
  const steContext = useAppSelector(selectSteContext);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Fetch contacts from API
  const {
    data: steConfig,
    isLoading,
    error: fetchError,
    refetch,
  } = useGetSteConfigQuery();

  const contacts = steConfig?.contacts || [];

  // Shared SIP context
  const sip = useSipContext();

  // Merge config
  const config = useMemo<HomeConfig>(
    () => ({
      ...defaultHomeConfig,
      ...customConfig,
      ...(steConfig?.web || {}),
    }),
    [customConfig, steConfig],
  );

  // Derived state
  const agentName = useMemo(() => {
    return steContext?.name || "Agent";
  }, [steContext]);

  // Connect to SIP server when on the Home screen
  useEffect(() => {
    if (steContext && !sip.isConnected && !sip.isRegistered && !sip.error) {
      sip.connect();
    }
  }, [steContext, sip]);

  // Error message - combine API and SIP errors
  const error = useMemo(() => {
    if (fetchError) {
      return "Failed to load contacts. Please try again.";
    }
    if (sip.error) {
      return sip.error;
    }
    return null;
  }, [fetchError, sip.error]);

  // Handle logout
  const handleLogout = useCallback(() => {
    Analytics.track("logout_clicked");
    sip.disconnect();
    dispatch(logout());
    navigate(Routes.LANDING);
  }, [dispatch, navigate, sip]);

  // Handle call button click - navigate to call screen
  const handleCall = useCallback(
    (contact: Contact) => {
      Analytics.track("contact_clicked", {
        contact_id: contact.id,
        contact_name:
          contact.contactName.en ||
          Object.values(contact.contactName)[0] ||
          "Unknown",
        contact_type: contact.contactType,
      });
      if (!sip.isMicrophoneGranted) {
        sip.requestMicrophoneAccess().then((granted) => {
          if (granted) {
            navigate(Routes.CALL, { state: { contact } });
          }
        });
      } else {
        navigate(Routes.CALL, { state: { contact } });
      }
    },
    [sip, navigate],
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetch();
    sip.clearError();
  }, [refetch, sip]);

  // Handle microphone request
  const handleRequestMicrophone = useCallback(() => {
    sip.requestMicrophoneAccess();
  }, [sip]);

  return {
    // State
    config,
    contacts,
    isLoading,
    error,
    agentName,
    isAuthenticated,
    // SIP State
    isSipConnected: sip.isConnected,
    isSipRegistered: sip.isRegistered,
    isMicrophoneGranted: sip.isMicrophoneGranted,

    // Actions
    handleLogout,
    handleCall,
    handleRefresh,
    handleRequestMicrophone,
  };
}
